import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Pill,
  Copy,
  AlertTriangle,
  Syringe,
  Clock,
  Activity,
  ShieldAlert,
  Zap,
  Check
} from 'lucide-react';

import { API_URL } from '../config';

export default function MedicationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [medication, setMedication] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMedication();
  }, [id]);

  const fetchMedication = async () => {
    try {
      const response = await fetch(`${API_URL}/api/medications/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMedication(data.medication);
      }
    } catch (error) {
      console.error('Error fetching medication:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <div className="skeleton h-8 w-48 mb-4"></div>
        <div className="skeleton h-64 rounded-xl"></div>
      </div>
    );
  }

  if (!medication) {
    return (
      <div className="p-4 md:p-6 text-center py-12">
        <p className="text-[hsl(var(--text-dim))]">Médicament non trouvé</p>
        <Button onClick={() => navigate('/medications')} className="mt-4">
          Retour aux médicaments
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate(-1)}
        className="text-[hsl(var(--text-dim))] hover:text-cyan-300"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour
      </Button>

      {/* Header */}
      <div className="glass-card rounded-xl p-6 neon-border">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-xl bg-cyan-400/20 flex items-center justify-center glow-cyan">
            <Pill className="w-8 h-8 text-cyan-300" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gradient-cyan">{medication.name}</h1>
            <p className="text-[hsl(var(--text-dim))]">{medication.brand_name}</p>
            <p className="text-sm text-cyan-400 mt-1">{medication.additional_info}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {medication.indications?.map((ind, i) => (
                <Badge key={i} className="bg-cyan-400/10 text-cyan-300 border border-cyan-400/30">
                  {ind}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Posologies */}
      <div className="glass-card rounded-xl p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Syringe className="w-5 h-5 text-cyan-400" />
          Posologies
        </h2>
        <div className="space-y-3">
          {medication.dosages?.map((dosage, i) => (
            <div key={i} className="p-4 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
              <div className="flex items-start justify-between">
                <div>
                  <Badge className="mb-2 bg-cyan-400/10 text-cyan-300">
                    {dosage.indication}
                  </Badge>
                  <div className="font-mono text-lg text-cyan-300">
                    {dosage.dose_per_kg ? (
                      <span>{dosage.dose_per_kg} {dosage.unit}/kg</span>
                    ) : (
                      <span>{dosage.fixed_dose} {dosage.unit}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-2 text-sm text-[hsl(var(--text-dim))]">
                    <span className="flex items-center gap-1">
                      <Syringe className="w-3 h-3" /> {dosage.route}
                    </span>
                    {dosage.frequency && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {dosage.frequency}
                      </span>
                    )}
                    {dosage.max_dose && (
                      <span className="flex items-center gap-1 text-amber-400">
                        <AlertTriangle className="w-3 h-3" /> Max: {dosage.max_dose} {dosage.unit}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(`${medication.name}: ${dosage.dose_per_kg ? `${dosage.dose_per_kg} ${dosage.unit}/kg` : `${dosage.fixed_dose} ${dosage.unit}`} ${dosage.route} ${dosage.frequency || ''}`)}
                  data-testid="dose-copy-button"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Contre-indications */}
      {medication.contraindications?.length > 0 && (
        <div className="glass-card rounded-xl p-6 border-red-500/30">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-red-400">
            <ShieldAlert className="w-5 h-5" />
            Contre-indications
          </h2>
          <ul className="space-y-2">
            {medication.contraindications.map((ci, i) => (
              <li key={i} className="flex items-center gap-2 text-red-300">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {ci}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Interactions */}
      {medication.interactions?.length > 0 && (
        <div className="glass-card rounded-xl p-6 border-amber-500/30">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-amber-400">
            <Zap className="w-5 h-5" />
            Interactions
          </h2>
          <ul className="space-y-2">
            {medication.interactions.map((int, i) => (
              <li key={i} className="flex items-center gap-2 text-amber-300">
                <Zap className="w-4 h-4 flex-shrink-0" />
                {int}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Surveillance */}
      {medication.surveillance?.length > 0 && (
        <div className="glass-card rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-cyan-400" />
            Surveillance
          </h2>
          <div className="flex flex-wrap gap-2">
            {medication.surveillance.map((item, i) => (
              <Badge key={i} variant="outline" className="text-cyan-300 border-cyan-400/30">
                <Check className="w-3 h-3 mr-1" />
                {item}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Preparation */}
      {medication.preparation && (
        <div className="glass-card rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-2">Préparation</h2>
          <p className="text-[hsl(var(--text-dim))]">{medication.preparation}</p>
        </div>
      )}
    </div>
  );
}
